import React, { useState, useEffect } from 'react';
import AddToFavourites from './AddToFavourites';

// Builds the dieline URL the breadcrumbs tab navigates to when returning from the
// layouts page. Dimensions and the other dieline options are restored from
// localStorage on the dieline, so only the combo state (combo_ids + the
// ask_values / combo_materials selections) is carried as query params the dieline
// controller reads. Dieline options must NOT ride along as ?options_json=: that
// branch is admin-only on the controller, so a logged-in non-admin who sent it
// was redirected to sign-in (then on to /recently-visited) instead of the dieline.
export const buildDielineUrlFromLayoutArgs = (action, args) => {
  if (!args) { return action; }

  let decodedParams;
  try {
    decodedParams = new URLSearchParams(atob(args));
  } catch (_e) {
    return action;
  }

  const queryParams = new URLSearchParams();

  if (decodedParams.has('combo_ids')) {
    queryParams.set('combo_ids', decodedParams.get('combo_ids'));
  }

  // ask_values[<rel>][<name>] and combo_materials[<rel>][<field>] are nested
  // bracket params; pass them through verbatim so Rails parses them as
  // params[:ask_values][rel][name] etc on the dieline page.
  decodedParams.forEach((value, key) => {
    if (key.startsWith('ask_values[') || key.startsWith('combo_materials[')) {
      queryParams.append(key, value);
    }
  });

  const queryString = queryParams.toString();
  return queryString.length > 0 ? `${action}?${queryString}` : action;
};

const MenuTabs = ({
  drawingId,
  breadcrumbs,
  threeDEnabled,
  layoutsEnabled,
  quoteBuilderV2Enabled,
  quoteBuilderEnabled,
  threeDPath,
  layoutsLandingPath,
  manufacturingCostCalculatorV2Path,
  dielinesApiDrawingTabPath,
  dielinesPath,
  noBookmark = false,
  activeTab = null,
  advancedOptionsFormVisible = false,
  rawArguments = null,
  onNavigate = null,
  comboGuestPresent = false,
  materialId = null,
  catalogName = null
}) => {

  // ShowDrawing fires combo_state_changed whenever its activeCombosByGuestName
  // toggles, so the dieline page's tab label flips between "Nesting" and
  // "Nesting & Manufacturing Cost Calculator" live as the user adds/removes a
  // guest. The initial value comes from the comboGuestPresent prop (server
  // computes it from @nav_sections on the layouts page; defaults to false on
  // the dieline page until the first event fires).
  const [comboGuestPresentLive, setComboGuestPresentLive] = useState(comboGuestPresent);

  useEffect(() => {
    const handler = (event) => {
      setComboGuestPresentLive(Boolean(event.detail && event.detail.hasGuests));
    };
    document.addEventListener('combo_state_changed', handler);
    return () => { document.removeEventListener('combo_state_changed', handler); };
  }, []);

  // material_id 'custom' means the user typed their own thickness; version-1
  // pricing cannot cost it, so the tab drops "ve Üretim Maliyeti" and reads just
  // "Yerleştirme" (issue #5151). The id arrives as an explicit prop (the layout
  // and 3D server renders know it) or encoded inside rawArguments. On the dieline
  // page the user changes it live in MaterialChooser, which fires
  // 'material_id_changed', so we keep it in state and update on that event.
  const detectCustomMaterial = (id) => {
    if (id === 'custom') { return true; }
    const args = rawArguments || window.rawArguments || '';
    if (!args) { return false; }
    try {
      return new URLSearchParams(atob(args)).get('material_id') === 'custom';
    } catch (_e) {
      return false;
    }
  };

  const [isCustomMaterial, setIsCustomMaterial] = useState(detectCustomMaterial(materialId));

  useEffect(() => {
    const handler = (event) => {
      setIsCustomMaterial((event.detail && event.detail.material_id) === 'custom');
    };
    document.addEventListener('material_id_changed', handler);
    return () => { document.removeEventListener('material_id_changed', handler); };
  }, []);

  // Common helper to check if navigation should be blocked
  const checkNavigationBlocked = (e, requireLogin = true) => {
    // Check if user is logged in (if login is required)
    if (requireLogin && !window.loggedIn) {
      if (typeof emitLogInRequired === 'function') {
        emitLogInRequired();
      }
      return true;
    }

    return false;
  };

  // Helper to navigate with dieline options - triggers redraw first to validate
  const navigateWithDielineOptions = (action, { newTab = false } = {}) => {
    // When opening in a new tab, create the blank tab synchronously here, inside
    // the click gesture, so the browser's popup blocker allows it. The redraw
    // validates the options asynchronously and DrawingSvg.onSuccess points this
    // tab at the final URL once it completes. Keeping the reference on
    // pendingMenuTabNavigation lets that async handler find it, and lets the
    // error handlers close it if validation fails. If the browser still blocks
    // the open, newTabWindow is null and onSuccess falls back to same-tab
    // navigation.
    const newTabWindow = newTab ? window.open('', '_blank') : null;

    // Store the pending navigation - DrawingSvg.onSuccess will handle navigation after redraw
    window.pendingMenuTabNavigation = {
      action: action,
      submit: 'dieline-options',
      advancedOptionsEnabled: advancedOptionsFormVisible,
      newTabWindow: newTabWindow
    };

    // Trigger redraw to validate values - navigation happens on success
    const redrawBtn = document.getElementById('redraw_btn');
    if (redrawBtn) {
      redrawBtn.click();
    }
  };

  // Helper to navigate with raw arguments
  const navigateWithRawArguments = (action) => {
    window.location.href = action + '/' + (rawArguments || window.rawArguments || '');
  };

  const navigateToDielineFromLayouts = (action) => {
    const args = rawArguments || window.rawArguments || '';
    window.location.href = buildDielineUrlFromLayoutArgs(action, args);
  };

  // Handler for breadcrumbs tab
  const handleBreadcrumbsClick = (e) => {
    e.preventDefault();

    // Don't navigate if already on breadcrumbs or no path available
    if (activeTab === 'breadcrumbs' || !dielinesPath) {
      return;
    }

    // Always require login for tab navigation
    if (checkNavigationBlocked(e, true)) {
      return;
    }

    // Custom navigation handler if provided
    if (onNavigate) {
      const submit = activeTab === 'dielines_api' ? 'direct' : 'dieline-options-from-layout';
      onNavigate(dielinesPath, submit, advancedOptionsFormVisible);
      return;
    }

    // Navigate based on where we're coming from
    if (activeTab === 'dielines_api') {
      window.location.href = dielinesPath;
    } else if (activeTab === 'layouts') {
      navigateToDielineFromLayouts(dielinesPath);
    } else {
      window.location.href = dielinesPath;
    }
  };

  // Handler for 3D Mockup tab
  const handle3DClick = (e) => {
    e.preventDefault();

    const action = e.currentTarget.dataset.action || threeDPath;

    if (!action || checkNavigationBlocked(e, true)) {
      return;
    }

    // Custom navigation handler if provided
    if (onNavigate) {
      const submitType = activeTab === 'dielines_api' ? 'dieline-options-from-api' : 'dieline-options';
      onNavigate(action, submitType, advancedOptionsFormVisible);
      return;
    }

    // Only the dieline page (activeTab 'breadcrumbs') has the #redraw_btn /
    // DrawingSvg.onSuccess that navigateWithDielineOptions drives; from there
    // open the 3D mockup in a new tab so the dieline and the 3D editor stay open
    // side by side. Every other page (layouts, Manufacturing Cost Calculator,
    // Dielines API) has no redraw form, so it navigates straight with the
    // options already in rawArguments.
    if (activeTab === 'breadcrumbs') {
      navigateWithDielineOptions(action, { newTab: true });
    } else {
      navigateWithRawArguments(action);
    }
  };

  // Handler for Layouts/Nesting tab
  const handleLayoutsClick = (e) => {
    e.preventDefault();

    const action = e.currentTarget.dataset.action || layoutsLandingPath;

    if (!action || activeTab === 'layouts' || checkNavigationBlocked(e, true)) {
      return;
    }

    // Custom navigation handler if provided
    if (onNavigate) {
      const submitType = activeTab === 'dielines_api' ? 'dieline-options-from-api' : 'dieline-options';
      onNavigate(action, submitType, advancedOptionsFormVisible);
      return;
    }

    // Only the dieline page has the redraw form navigateWithDielineOptions
    // drives; every other page navigates straight with rawArguments.
    if (activeTab === 'breadcrumbs') {
      navigateWithDielineOptions(action);
    } else {
      navigateWithRawArguments(action);
    }
  };

  // The Manufacturing Cost Calculator tab lands on the /mcc index, which is the V2 index path with the
  // prefix swapped -- so no new prop is needed from the many views that render these tabs.
  const handleFrankTabClick = (e) => {
    e.preventDefault();

    const frankPath = manufacturingCostCalculatorV2Path
      && manufacturingCostCalculatorV2Path.replace('/manufacturing-cost-calculator', '/mcc');
    const action = e.currentTarget.dataset.action || frankPath;

    if (!action || activeTab === 'quote_builder_frank' || checkNavigationBlocked(e, true)) {
      return;
    }

    // Custom navigation handler if provided
    if (onNavigate) {
      onNavigate(action, 'dieline-options', advancedOptionsFormVisible);
      return;
    }

    // Only the dieline page has the #redraw_btn / DrawingSvg.onSuccess that
    // drives navigateWithDielineOptions; from any other page (layouts, Dielines
    // API) it would silently do nothing, so navigate straight with the options
    // already in rawArguments, the way the 3D tab does.
    if (activeTab === 'breadcrumbs') {
      navigateWithDielineOptions(action);
    } else {
      navigateWithRawArguments(action);
    }
  };

  // Handler for Dielines API tab
  const handleDielinesApiClick = (e) => {
    e.preventDefault();

    const action = e.currentTarget.dataset.action || dielinesApiDrawingTabPath;

    if (!action || activeTab === 'dielines_api') {
      return;
    }

    // Always require login for tab navigation
    if (checkNavigationBlocked(e, true)) {
      return;
    }

    // Custom navigation handler if provided
    if (onNavigate) {
      onNavigate(action, 'dieline-options', advancedOptionsFormVisible);
      return;
    }

    // Only the dieline page has the #redraw_btn navigateWithDielineOptions
    // drives; from the layouts and Manufacturing Cost Calculator pages it was a
    // silent no-op, so navigate straight with the options already in
    // rawArguments (the /dielines-api/:id/:slug/:arguments route reads them).
    if (activeTab === 'breadcrumbs') {
      navigateWithDielineOptions(action);
    } else {
      navigateWithRawArguments(action);
    }
  };

  const getTabClassName = (tabName) => {
    const baseClass = 'shortcode_tab_item_title';
    const isActive = activeTab === tabName;
    return `${baseClass} ${isActive ? 'inactivetab' : 'active'}`;
  };

  return (
    <div className="container" id="menuTabs">
      <div className="row" style={{ borderLeft: '1px solid #eee' }}>
        <div className="col-lg-12" style={{ marginLeft: '-15px', marginBottom: '15px' }}>
          <div className="all_heads_cont">

            {!noBookmark && (
              <AddToFavourites defaultId={drawingId} />
            )}

            <div
              className={`shortcode_tab_item_title ${activeTab === 'breadcrumbs' ? 'inactivetab largeText16' : 'active'}`}
              style={{ borderLeft: '0px solid #eee', marginTop: '0px' }}
              onClick={handleBreadcrumbsClick}
            >
              <span id="header_breadcrumbs">{breadcrumbs}</span>
              {catalogName && (
                <span
                  className="js-copy-catalog-name"
                  data-catalog-name={catalogName}
                  data-copy-source="dielines_header"
                  role="button"
                  tabIndex={0}
                  title={t('three_d.copy_catalog_name')}
                  style={{ cursor: 'pointer', marginLeft: '6px' }}
                >
                  <i className="fa fa-copy" aria-hidden="true" style={{ fontSize: '0.8em' }}></i>
                </span>
              )}
            </div>

            {threeDEnabled && (
              <div
                className={getTabClassName('3d')}
                id="menu_tab_3d"
                style={{ marginTop: '0px' }}
                onClick={handle3DClick}
              >
                {t('three_d.three_d_view')}
              </div>
            )}

            {quoteBuilderV2Enabled && !isCustomMaterial && (
              <div
                className={getTabClassName('quote_builder_frank')}
                id="menu_tab_nesting_frank"
                style={{ marginTop: '0px' }}
                onClick={handleFrankTabClick}
              >
                {t('nav.quote_builder_page_title')}
              </div>
            )}

            {layoutsEnabled && (
              <div
                className={getTabClassName('layouts')}
                id="menu_tab_nesting"
                style={{ marginTop: '0px' }}
                onClick={handleLayoutsClick}
              >
                {(quoteBuilderEnabled && !comboGuestPresentLive && !isCustomMaterial && !quoteBuilderV2Enabled)
                  ? t('layouts.layouts_and_quote_builder_view')
                  : t('layouts.layouts_view')
                }
              </div>
            )}

            <div
              className={`shortcode_tab_item_title ${activeTab === 'dielines_api' ? 'inactive largeText16' : 'active'}`}
              id="menu_tab_dielines_api"
              style={{ marginTop: '0px' }}
              onClick={handleDielinesApiClick}
            >
              {t('nav.dielines_api')}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuTabs;